import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ConfirmDialog from "components/template/confirm-dialog";
import ActivityAccordion from "./activity-accordion";
import ActivityDayList from "./activity-day-list";
import ActivityForm from "./activity-form";
import { buildMonthDays, capitalizeLabel, groupActivitiesByDate } from "./calendar";
import { DetailHeader, DetailPane, EditorCard, Workspace } from "./theme";

const noop = () => {};

const errorMessage = (error) => error?.message || "Impossibile completare l'operazione";

// Mirrors RUN_STATUS in server/src/ai/task/ai-task-run.ts: comparing against
// an invented string here would make the wait below never end.
const RUN_STATUS_PENDING = "pending";
const RUN_STATUS_RUNNING = "running";
const RUN_STATUS_ERROR = "error";

// How long, and how many times, to poll the check before giving up. The
// activity is already saved either way -- this only bounds how long the
// editor waits for the agent's opinion on it.
//
// task-runner.ts (enqueueAndStart) says that when there is no free
// concurrency slot the run stays "pending" and "the cron picks the row up
// within the minute", naming by example the exact load this has to survive:
// twenty people saving a report at 17:00. Budget for up to a minute of
// queueing plus the model call itself -- about two minutes in total, polled
// every 3 seconds -- so the happy path stays reachable under that load
// instead of expiring by construction.
export const CHECK_POLL_INTERVAL_MS = 3000;
export const CHECK_MAX_ATTEMPTS = 40;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Turns a finished check's raw response into one of the five outcomes the
 * employee can be told apart: has suggestions, looks complete, looks
 * incomplete but with nothing specific to point at, failed, or (added by the
 * poller below) timed out. `looks_complete` missing or null is the server's
 * own signal for "could not tell", so it is failure here too, never silently
 * folded into "nothing to say".
 *
 * `looks_complete: false` with an empty `suggestions` array is allowed by
 * the output schema (Patch34-ActivityCheckPrompt.ts): the model judged the
 * report incomplete without managing to say what is missing. That is its
 * own outcome, not "suggestions" with zero items -- the latter would render
 * a "here is what to fix" header over nothing to read under it.
 */
const outcomeFromCheckResponse = (response) => {
  const suggestions = Array.isArray(response?.suggestions) ? response.suggestions : [];
  const looksComplete = response?.looks_complete;
  if (response?.status === RUN_STATUS_ERROR || looksComplete === null || looksComplete === undefined) {
    return { outcome: "failed", looksComplete: null, suggestions: [] };
  }
  if (looksComplete && suggestions.length === 0) {
    return { outcome: "complete", looksComplete: true, suggestions: [] };
  }
  if (!looksComplete && suggestions.length === 0) {
    return { outcome: "incomplete", looksComplete: false, suggestions: [] };
  }
  return { outcome: "suggestions", looksComplete, suggestions };
};

/**
 * Polls the check until it leaves pending/running, or gives up after
 * CHECK_MAX_ATTEMPTS. A poll that itself throws is treated the same as a
 * check the server reports as failed -- from here the two are
 * indistinguishable, and both mean "no opinion available".
 */
const pollCheck = async (api, runId) => {
  for (let attempt = 1; attempt <= CHECK_MAX_ATTEMPTS; attempt += 1) {
    let response;
    try {
      response = await api.checkStatus(runId);
    } catch (error) {
      return { outcome: "failed", looksComplete: null, suggestions: [] };
    }
    if (response?.status !== RUN_STATUS_PENDING && response?.status !== RUN_STATUS_RUNNING) {
      return outcomeFromCheckResponse(response);
    }
    if (attempt < CHECK_MAX_ATTEMPTS) {
      await wait(CHECK_POLL_INTERVAL_MS);
    }
  }
  return { outcome: "timeout", looksComplete: null, suggestions: [] };
};

export default function EmployeeActivities({
  addNotice = noop,
  api,
  employeeId,
  employeeOptions = [],
  mode,
  onEmployeeChange,
}) {
  const [month, setMonth] = useState(() => moment().startOf("month"));
  const [today, setToday] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(
    mode === "admin" && Number.isInteger(employeeId) ? employeeId : null
  );
  // Identifies which editor a poll started for. Bumped whenever the editor
  // is opened or closed, so a check's answer that arrives after its editor
  // is gone can tell it is stale and skip updating checking/checkResult,
  // instead of landing on whatever editor happens to be open by then.
  const pollTokenRef = useRef(0);

  const loadActivities = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    setLoadError(false);
    const range = {
      from: month.clone().startOf("month").format("YYYY-MM-DD"),
      to: month.clone().endOf("month").format("YYYY-MM-DD"),
    };
    if (mode === "admin" && selectedEmployeeId !== null) {
      range.employeeId = selectedEmployeeId;
    }
    try {
      const [list, availableQuotes] = await Promise.all([api.list(range), api.quotes()]);
      const nextItems = Array.isArray(list?.items) ? list.items : [];
      const nextToday = list?.today || "";
      const nextDays = buildMonthDays(month, nextToday);
      setItems(nextItems);
      setQuotes(Array.isArray(availableQuotes) ? availableQuotes : []);
      setToday(nextToday);
      setSelectedDate((current) =>
        nextDays.some((day) => day.date === current) ? current : nextDays[0]?.date || null
      );
    } catch (error) {
      setLoadError(true);
      addNotice("error", errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [addNotice, api, mode, month, selectedEmployeeId]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const days = useMemo(() => buildMonthDays(month, today), [month, today]);
  const activitiesByDate = useMemo(() => groupActivitiesByDate(items), [items]);
  const selectedItems = selectedDate ? activitiesByDate[selectedDate] || [] : [];
  const selectedDay = days.find((day) => day.date === selectedDate);
  const canAdd = (Boolean(selectedDate) && mode !== "admin") || (
    Boolean(selectedDate) && mode === "admin" && selectedEmployeeId !== null
  );
  const canGoNext = Boolean(today) && month.isBefore(moment(today, "YYYY-MM-DD", true), "month");
  const canAddNow = canAdd && !editor;

  const openEditor = (nextEditor) => {
    if (!editor) {
      // A fresh editor starts from a clean slate: nothing was saved yet
      // through it, so no message about a save may carry over.
      pollTokenRef.current += 1;
      setChecking(false);
      setCheckResult(null);
      setEditor(nextEditor);
    }
  };

  const closeEditor = () => {
    // Bumping the token here, not just resetting the flags, is what makes a
    // poll already in flight for this editor a no-op when it eventually
    // answers: see the guard in saveActivity.
    pollTokenRef.current += 1;
    setChecking(false);
    setCheckResult(null);
    setEditor(null);
  };

  const saveActivity = async (payload) => {
    if (!editor) return;
    setSaving(true);
    setCheckResult(null);
    setChecking(false);
    const wasCreate = editor.type === "create";
    let saved;
    try {
      const request =
        mode === "admin" ? { ...payload, employee_id: selectedEmployeeId } : payload;
      saved = editor.type === "edit" ? await api.update(editor.id, request) : await api.create(request);
    } catch (error) {
      setSaving(false);
      addNotice("error", errorMessage(error));
      return;
    }
    setSaving(false);

    // Absent for the admin API (no route there) and whenever queueing the
    // check itself failed server-side -- the save already succeeded either
    // way, so this is the non-blocking path the backend treats it as.
    const checkRunId = saved?.check_run_id;
    if (!checkRunId) {
      closeEditor();
      await loadActivities();
      return;
    }

    // The activity is saved; only the check is still pending. The editor
    // stays open so the employee sees what the check found, not "closed" as
    // if nothing more were coming. The token identifies this particular
    // poll so a later close/reopen makes its eventual answer a no-op
    // instead of landing on whatever editor happens to be open by then.
    pollTokenRef.current += 1;
    const token = pollTokenRef.current;
    setChecking(true);
    const result = await pollCheck(api, checkRunId);
    const isCurrent = pollTokenRef.current === token;
    if (isCurrent) {
      setChecking(false);
      setCheckResult(result);
    }
    // The activity was already saved before the check started: it belongs
    // in the list whether the check finished, failed, or ran out of time --
    // regardless of whether its editor is still the one open.
    await loadActivities();
    // Promote a freshly created row to an edit of that row now that the
    // reload above has put it in `items`: pressing Salva again -- "I'm
    // missing the time spent, let me add it", the interaction this screen
    // invites -- then calls api.update instead of api.create, so it stops
    // duplicating the activity.
    if (isCurrent && wasCreate && Number.isInteger(saved?.id)) {
      setEditor({ type: "edit", id: saved.id });
    }
  };

  const confirmDelete = async (confirmed) => {
    if (!confirmed) {
      setDeleteCandidate(null);
      return;
    }
    try {
      await api.remove(deleteCandidate.id);
      setDeleteCandidate(null);
      await loadActivities();
    } catch (error) {
      addNotice("error", errorMessage(error));
    }
  };

  const selectEmployee = (value) => {
    const nextEmployeeId = value === "" ? null : Number(value);
    setSelectedEmployeeId(nextEmployeeId);
    onEmployeeChange?.(nextEmployeeId);
  };

  return (
    <Workspace>
      <ActivityDayList
        activityCounts={Object.keys(activitiesByDate).reduce((counts, date) => {
          counts[date] = activitiesByDate[date].length;
          return counts;
        }, {})}
        canAdd={canAddNow}
        canGoNext={canGoNext}
        days={days}
        monthLabel={month.clone().locale("it").format("MMMM YYYY")}
        onAdd={() => openEditor({ type: "create" })}
        onNextMonth={() => canGoNext && setMonth((current) => current.clone().add(1, "month"))}
        onPreviousMonth={() => setMonth((current) => current.clone().subtract(1, "month"))}
        onSelect={setSelectedDate}
        selectedDate={selectedDate}
      />
      <DetailPane>
        {mode === "admin" ? (
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="employee-activity-employee-label">Dipendente</InputLabel>
              <Select
                label="Dipendente"
                labelId="employee-activity-employee-label"
                onChange={(event) => selectEmployee(event.target.value)}
                value={selectedEmployeeId || ""}
              >
                <MenuItem value="">Tutti i dipendenti</MenuItem>
                {employeeOptions.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : null}
        {loading ? <Typography aria-label="Caricamento attività" role="status">Caricamento attività</Typography> : null}
        {loadError ? <Typography role="alert">Impossibile caricare le attività.</Typography> : null}
        {!loadError ? (
          <DetailHeader>
            <div>
              {selectedDay ? (
                <Typography data-testid="detail-date" variant="h6">
                  {capitalizeLabel(selectedDay.label)}
                </Typography>
              ) : null}
              <Typography variant="body2">{selectedItems.length} attività</Typography>
            </div>
            {canAddNow ? (
              <IconButton
                aria-label="Aggiungi attività"
                onClick={() => openEditor({ type: "create" })}
                size="small"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            ) : null}
          </DetailHeader>
        ) : null}
        {!loading && !loadError && selectedDate && selectedItems.length === 0 ? (
          <Typography>Nessuna attività per questo giorno.</Typography>
        ) : null}
        {editor?.type === "create" && selectedDate ? (
          <EditorCard data-testid="activity-editor-card">
            <ActivityForm
              checking={checking}
              checkResult={checkResult}
              date={selectedDate}
              onCancel={closeEditor}
              onSave={saveActivity}
              quotes={quotes}
              saving={saving}
            />
          </EditorCard>
        ) : null}
        {selectedItems.map((activity) => (
          <ActivityAccordion
            activity={activity}
            checking={checking}
            checkResult={checkResult}
            editing={editor?.type === "edit" && editor.id === activity.id}
            key={activity.id}
            mode={mode}
            onCancel={closeEditor}
            onDelete={(candidate) => !editor && setDeleteCandidate(candidate)}
            onEdit={(candidate) => openEditor({ type: "edit", id: candidate.id })}
            onSave={saveActivity}
            quotes={quotes}
            saving={saving}
          />
        ))}
      </DetailPane>
      {deleteCandidate ? (
        <ConfirmDialog
          description="L'attività verrà eliminata definitivamente."
          handleClose={confirmDelete}
          title="Eliminare attività?"
        />
      ) : null}
    </Workspace>
  );
}
